import React from 'react'
import ReactDOMServer from 'react-dom/server'
import { VoucherResponse } from '@/app/types/voucher'
import { Party } from '@/app/types/party'
import { CompanyDetails } from '@/app/services/thermalPrinterService'
import { format } from 'date-fns'
import { PartyClosingBalanceResponse } from '@/app/types/reports'

/**
 * Helper function to format site address from individual fields
 */
function formatSiteAddress(site: VoucherResponse['site']): { addressLine1: string; addressLine2: string } {
  if (!site) return { addressLine1: '', addressLine2: '' }

  const line1Parts = []
  if (site.address_line1) line1Parts.push(site.address_line1)
  if (site.address_line2) line1Parts.push(site.address_line2)

  const line2Parts = []
  if (site.city) line2Parts.push(site.city)
  if (site.state) line2Parts.push(site.state)
  if (site.postal_code) line2Parts.push(site.postal_code)

  return {
    addressLine1: line1Parts.join(', '),
    addressLine2: line2Parts.join(', ')
  }
}

/**
 * Converts the ThermalReceipt data to HTML string for preview
 * This ensures preview matches the actual print output
 */
export function thermalReceiptToHtml(
  invoice: VoucherResponse,
  party: Party | undefined,
  company: CompanyDetails,
  paperSize: '80mm' | '58mm' | '44mm' = '80mm',
  charactersPerLine?: number,
  closingBalance?: PartyClosingBalanceResponse
): string {
  // Debug log to check invoice data
  console.log('[ThermalReceipt] Invoice data:', {
    hasItems: invoice.items?.length || 0,
    firstItem: invoice.items?.[0],
    hasGstBreakdown: invoice.items?.[0]?.gst_breakdown,
    taxAmount: invoice.items?.[0]?.tax_amount
  })
  // Use charactersPerLine if provided, otherwise use default based on paper size
  const width = charactersPerLine || (paperSize === '80mm' ? 48 : paperSize === '58mm' ? 36 : 28)

  // Get site address or fallback to company address
  const siteAddress = formatSiteAddress(invoice.site)
  const displayAddress1 = siteAddress.addressLine1 || company.address
  const displayAddress2 = siteAddress.addressLine2 || company.cityStatePin
  const displayPhone = invoice.site?.contact_number || company.phone
  const displayGstin = invoice.site?.gstin || company.gstin
  
  // Calculate totals
  let subtotal = 0
  let totalTax = 0
  let totalDiscount = 0
  let deliveryCharge = Number(invoice.delivery_charge || 0)
  let grandTotal = 0
  
  const taxBreakdown: { [key: number]: { amount: number, taxableAmount: number } } = {}
  
  invoice.items?.forEach((item) => {
    const quantity = Number(item.quantity || 0)
    const rate = Number(item.rate || 0)
    const discountAmount = Number(item.discount_amount || 0)
    const taxAmount = Number(item.tax_amount || 0)
    const totalAmount = Number(item.total_amount || item.amount || 0)
    const taxableAmount = Number(item.taxable_amount || 0)
    
    // For subtotal, use taxable amount + discount (before tax)
    subtotal += taxableAmount + discountAmount
    totalDiscount += discountAmount
    totalTax += taxAmount
    grandTotal += totalAmount
    
    // Group tax by rate using GST breakdown or tax_rate_id
    if (taxAmount > 0) {
      let totalRate = 0
      
      // Try to get rate from GST breakdown first
      if (item.gst_breakdown && item.gst_breakdown.length > 0) {
        totalRate = item.gst_breakdown.reduce((sum, gst) => {
          // Handle both 'rate' and 'rate_percent' fields
          const rate = gst.rate || (gst as any).rate_percent || 0
          return sum + rate
        }, 0)
      } else if (item.tax_rate_id) {
        // If no GST breakdown, estimate rate from tax amount and taxable amount
        const taxableAmount = Number(item.taxable_amount || 0)
        if (taxableAmount > 0) {
          totalRate = (taxAmount / taxableAmount) * 100
        }
      }
      
      const taxableAmount = Number(item.taxable_amount || 0)
      
      if (totalRate > 0) {
        // Round rate to nearest 0.5 for grouping (to handle 2.5%, 5%, etc.)
        const roundedRate = Math.round(totalRate * 2) / 2
        if (!taxBreakdown[roundedRate]) {
          taxBreakdown[roundedRate] = { amount: 0, taxableAmount: 0 }
        }
        taxBreakdown[roundedRate].amount += taxAmount
        taxBreakdown[roundedRate].taxableAmount += taxableAmount
      }
    }
  })
  
  // Use the actual invoice total from backend (with rounding applied)
  // If backend doesn't have total_amount, calculate it including delivery charge
  const total = Number(invoice.total_amount || (grandTotal + deliveryCharge))
  
  // Helper function to center text
  const center = (text: string, width: number) => {
    const padding = Math.max(0, Math.floor((width - text.length) / 2))
    return ' '.repeat(padding) + text
  }
  
  // Helper function to create a row with left and right text
  const row = (left: string, right: string, width: number) => {
    // Ensure we don't exceed the width
    const maxLeftLength = width - right.length - 1
    const truncatedLeft = left.length > maxLeftLength ? left.substring(0, maxLeftLength) : left
    const space = width - truncatedLeft.length - right.length
    return truncatedLeft + ' '.repeat(Math.max(1, space)) + right
  }
  
  // Build HTML
  let html = '<div style="font-family: monospace; white-space: pre; line-height: 1.3;">'
  
  // Company Header
  html += `<strong>${center(company.name.toUpperCase(), width)}</strong>\n`
  if (displayAddress1) html += center(displayAddress1, width) + '\n'
  if (displayAddress2) html += center(displayAddress2, width) + '\n'
  if (displayPhone) html += center(`Ph No.: ${displayPhone}`, width) + '\n'
  if (displayGstin) html += center(`GSTIN: ${displayGstin}`, width) + '\n'
  
  html += '-'.repeat(width) + '\n'
  
  // Invoice Header
  html += `<strong>${center('Invoice', width)}</strong>\n`
  html += `<strong>${center(party?.name?.toUpperCase() || 'CASH', width)}</strong>\n`
  
  html += 'Bill To:\n'
  if (party?.name) html += party.name.toUpperCase() + '\n'
  if (party?.billing_address) html += party.billing_address + '\n'
  if (party?.phone) html += 'Ph: ' + party.phone + '\n'
  
  html += row('', `Date: ${format(new Date(invoice.transaction_date), 'dd/MM/yyyy')}`, width) + '\n'
  html += row('', `Invoice No: ${invoice.voucher_type?.prefix || 'FY'}${format(new Date(), 'yy')}-${invoice.voucher_number}`, width) + '\n'
  
  html += '-'.repeat(width) + '\n'
  
  // Items Header
  const headerLeft = '# Name'
  const headerRight = 'Qty   Price  Amount'
  html += row(headerLeft, headerRight, width) + '\n'
  html += '-'.repeat(width) + '\n'
  
  // Sort items alphabetically by name
  const sortedItems = [...(invoice.items || [])].sort((a, b) => {
    const nameA = (a.item?.name || a.name || a.description || 'Item').toUpperCase()
    const nameB = (b.item?.name || b.name || b.description || 'Item').toUpperCase()
    return nameA.localeCompare(nameB)
  })
  
  // Items
  sortedItems.forEach((item, index) => {
    const quantity = Number(item.quantity || 0)
    const rate = Number(item.rate || 0)
    // Use the actual amount from item which includes tax calculations
    const amount = Number(item.total_amount || item.amount || 0)
    const itemName = item.item?.name || item.name || item.description || 'Item'
    // Tamil name from additional_fields
    const tamilName = item.item?.additional_fields?.name_tamil || ''
    // FSSI number might be in description or additional fields
    const fssiNumber = item.description?.includes('FSSI') ? item.description : ''
    
    // Calculate right-aligned columns
    const qtyStr = quantity.toString().padStart(3)
    const priceStr = rate.toFixed(2).padStart(7)
    const amountStr = amount.toFixed(2).padStart(7)
    const rightColumns = `${qtyStr} ${priceStr} ${amountStr}`
    
    // Calculate available space for item name
    const itemNameSpace = width - rightColumns.length - 3 // 3 for index and space
    const fullItemName = `${index + 1} ${itemName.toUpperCase()}`
    
    // Wrap item name if it's too long
    if (fullItemName.length <= itemNameSpace) {
      // Item name fits on one line
      html += row(fullItemName, rightColumns, width) + '\n'
    } else {
      // Item name needs wrapping
      let currentLine = fullItemName.substring(0, itemNameSpace)
      html += row(currentLine, rightColumns, width) + '\n'

      let remainder = fullItemName.substring(itemNameSpace)
      while (remainder.length > 0) {
        currentLine = '  ' + remainder.substring(0, itemNameSpace - 2)
        remainder = remainder.substring(itemNameSpace - 2)
        html += currentLine.trimEnd() + '\n'
      }
    }

    // Add Tamil name on new line if present
    if (tamilName) {
      const tamilDisplay = `  (${tamilName})`
      // Wrap Tamil name if it's too long
      if (tamilDisplay.length <= width) {
        html += tamilDisplay + '\n'
      } else {
        let currentLine = tamilDisplay.substring(0, width)
        html += currentLine + '\n'
        let remainder = tamilDisplay.substring(width)
        while (remainder.length > 0) {
          currentLine = '  ' + remainder.substring(0, width - 2)
          remainder = remainder.substring(width - 2)
          html += currentLine.trimEnd() + '\n'
        }
      }
    }

    // Add FSSI number on new line if present
    if (fssiNumber) {
      html += `  FSSI: ${fssiNumber}\n`
    }
  })
  
  html += '-'.repeat(width) + '\n'
  
  // Summary
  const totalQty = sortedItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0) || 0
  html += row('Total Items', totalQty.toString(), width) + '\n'
  html += '\n'
  html += row('Total', `: ${total.toFixed(2)}`, width) + '\n'
  
  // Tax Breakdown
  if (totalTax > 0) {
    html += '\n'
    
    // If we have detailed tax breakdown, show it
    if (Object.keys(taxBreakdown).length > 0) {
      Object.entries(taxBreakdown).forEach(([rate, data]) => {
        const cgst = data.amount / 2
        const sgst = data.amount / 2
        const taxRate = Number(rate) / 2
        
        // Only show tax breakdown if rate is valid
        if (!isNaN(taxRate) && taxRate > 0) {
          html += row(`SGST @ ${taxRate.toFixed(1)}%`, `: ${sgst.toFixed(2)}`, width) + '\n'
          html += row(`CGST @ ${taxRate.toFixed(1)}%`, `: ${cgst.toFixed(2)}`, width) + '\n'
        }
      })
    } else {
      // If no breakdown available but we have tax, show total tax
      const cgst = totalTax / 2
      const sgst = totalTax / 2
      html += row('SGST', `: ${sgst.toFixed(2)}`, width) + '\n'
      html += row('CGST', `: ${cgst.toFixed(2)}`, width) + '\n'
    }
  }
  
  if (totalDiscount > 0) {
    html += row('Discount', `: -${totalDiscount.toFixed(2)}`, width) + '\n'
  }
  
  if (deliveryCharge > 0) {
    html += row('Delivery Charge', `: ${deliveryCharge.toFixed(2)}`, width) + '\n'
  }
  
  html += '-'.repeat(width) + '\n'
  
  html += '-'.repeat(width) + '\n'
  
  // Party Closing Balance
  if (closingBalance && party) {
    html += '\n'
    const balanceAmount = parseFloat(closingBalance.closing_balance).toFixed(2)
    html += row('Balance', `: ₹${balanceAmount} ${closingBalance.closing_balance_type}`, width) + '\n'
    html += '\n'
  }
  
  // Footer
  html += 'Terms & Conditions\n'
  html += 'Thanks for doing business with us!\n'
  
  html += '</div>'
  
  return html
}